import React from 'react'

import { Select, Stack, Text, TextInput } from '@mantine/core'
import cn from 'classnames'

import { useMst } from '#hooks'
import {
  SlotConfig,
  staticSlotContentType,
  StaticSlotContentType,
} from '#util/zodSchemas/printPreferences'

import styles from './SlotSelect.module.scss'

type Slot = {
  value: StaticSlotContentType | 'custom'
  label: string
}

const SLOT_OPTIONS: Array<Slot> = [
  { value: 'date', label: 'Date' },
  { value: 'timestamp', label: 'Date and time' },
  { value: 'page-number', label: 'Page number' },
  { value: 'page-number-total', label: 'Page number/total' },
  { value: 'revisions', label: 'Active revision' },
  { value: 'revisions-collated', label: 'Collated revision' },
  { value: 'duration', label: 'Script duration' },
  { value: 'short-title', label: 'Title' },
  { value: 'full-title', label: 'Title and section info' },
  { value: 'custom', label: 'Custom text' },
]

export const SlotSelect = ({
  label,
  config,
  setContent,
  disabled,
  inactive = false,
  isFooter = false,
}: {
  label: string
  config?: SlotConfig
  setContent: (value: SlotConfig | undefined) => void
  disabled?: boolean
  inactive?: boolean
  isFooter?: boolean
}) => {
  const mst = useMst()

  const slotData = SLOT_OPTIONS.filter((slot) => {
    if (slot.value === 'revisions' || slot.value === 'revisions-collated') {
      return mst.view.revisionsEnabled
    }
    return true
  })

  const content =
    config?.slotType === 'literal'
      ? 'custom'
      : config?.slotType === 'static'
        ? config.contentType
        : null

  const [customText, setCustomText] = React.useState(
    config?.slotType === 'literal' ? config.text : '',
  )

  const setSlotType = (value: string | null) => {
    mst.trackEvent('PDF_PREFERENCES_UPDATED', { old: content, value, isFooter })
    if (value === 'custom') {
      setContent({
        slotType: 'literal',
        text: '',
      })
    } else {
      const parsed = staticSlotContentType.safeParse(value)
      if (parsed.success) {
        setContent({
          slotType: 'static',
          contentType: parsed.data,
        })
      } else {
        setContent(undefined)
      }
    }
  }

  const submitCustomText = () => {
    if (config?.slotType === 'literal') {
      setContent({
        slotType: 'literal',
        text: customText,
      })
    }
  }

  return (
    <Stack gap={8} className={styles.slotSelect_stack}>
      <Text fw="bold">{label}</Text>
      <Select
        classNames={{
          input: cn(styles.slotSelect_input, {
            [styles.slotSelect_input___inactive]: inactive,
          }),
        }}
        clearable
        disabled={disabled}
        value={content}
        onChange={setSlotType}
        data={slotData}
      />
      {config?.slotType === 'literal' && (
        <TextInput
          placeholder="Your custom text"
          size="sm"
          value={customText}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submitCustomText()
            }
          }}
          onChange={(e) => setCustomText(e.target.value)}
          onBlur={submitCustomText}
        />
      )}
    </Stack>
  )
}
